import type {
  ActivityDto,
  CenterDto,
  DeskDto,
  ExportDto,
  ExportQuery,
  GroupDto,
  MeDto,
  StudentDto,
  StudentProfileDto,
  TeamMemberDto,
  TodayDto,
} from '@hudhuria/shared';
import { addDays, can, localDate, type Permission } from '@hudhuria/shared';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api, ok } from '~/lib/api';
import { useSession } from '~/session/session';
import {
  usePendingActivities,
  usePendingCheckins,
  usePendingGroups,
  usePendingRegistrations,
  usePendingRollCalls,
} from './pending';

export const qk = {
  me: ['me'] as const,
  students: ['students'] as const,
  student: (id: string) => ['student', id] as const,
  activities: (centerId: string) => ['activities', centerId] as const,
  groups: (centerId: string) => ['groups', centerId] as const,
  today: (centerId: string, date: string) => ['today', centerId, date] as const,
  team: ['team'] as const,
  desks: ['desks'] as const,
  export: (centerId: string, q: ExportQuery) => ['export', centerId, q.type, q.range, q.scope] as const,
};

export const meQuery = queryOptions({
  queryKey: qk.me,
  queryFn: () => ok<MeDto>(api.auth.me.$get()),
  staleTime: 60_000,
});

export const studentsQuery = queryOptions({
  queryKey: qk.students,
  queryFn: () => ok<StudentDto[]>(api.students.$get()),
  staleTime: 60_000,
});

export const activitiesQuery = (centerId: string, today: string) =>
  queryOptions({
    queryKey: qk.activities(centerId),
    queryFn: () =>
      ok<ActivityDto[]>(
        api.centers[':centerId'].activities.$get({
          param: { centerId },
          query: { from: today, to: addDays(today, 6) },
        }),
      ),
    staleTime: 60_000,
  });

export const groupsQuery = (centerId: string) =>
  queryOptions({
    queryKey: qk.groups(centerId),
    queryFn: () => ok<GroupDto[]>(api.centers[':centerId'].groups.$get({ param: { centerId } })),
    // Short, so a group an admin just assigned shows up for the teacher on their next look.
    staleTime: 30_000,
  });

export const todayQuery = (centerId: string, date: string) =>
  queryOptions({
    queryKey: qk.today(centerId, date),
    queryFn: () =>
      ok<TodayDto>(api.centers[':centerId'].today.$get({ param: { centerId }, query: { date } })),
    staleTime: 20_000,
    refetchInterval: 60_000,
  });

export const profileQuery = (id: string) =>
  queryOptions({
    queryKey: qk.student(id),
    queryFn: () => ok<StudentProfileDto>(api.students[':id'].$get({ param: { id } })),
    staleTime: 30_000,
  });

export const teamQuery = queryOptions({
  queryKey: qk.team,
  queryFn: () => ok<TeamMemberDto[]>(api.team.$get()),
  staleTime: 30_000,
});

export const desksQuery = queryOptions({
  queryKey: qk.desks,
  queryFn: () => ok<DeskDto[]>(api.desks.$get()),
  staleTime: 30_000,
});

export const exportQuery = (centerId: string, q: ExportQuery) =>
  queryOptions({
    queryKey: qk.export(centerId, q),
    queryFn: () => ok<ExportDto>(api.reports.export.$get({ query: { ...q, centerId } })),
    staleTime: 15_000,
  });

// ---------------- session-aware hooks ----------------

export function useMe() {
  const { token } = useSession();
  return useQuery({ ...meQuery, enabled: !!token });
}

export function useCan(perm: Permission) {
  return can(useMe().data?.staff.role, perm);
}

/** Today's date in the institution's timezone. */
export function useToday(): string {
  const tz = useMe().data?.institution.timezone ?? 'Africa/Nairobi';
  return localDate(new Date(), tz);
}

export function useCenter(): (CenterDto & { enrolledLive: number }) | null {
  const me = useMe().data;
  const { centerId } = useSession();
  const roster = useRoster();
  const center = me?.centers.find((c) => c.id === centerId) ?? null;
  return useMemo(() => {
    if (!center) return null;
    const enrolledLive = roster.data
      ? roster.data.filter((s) => s.homeCenterId === center.id).length
      : center.enrolled;
    return { ...center, enrolledLive };
  }, [center, roster.data]);
}

export function useCenterId(): string {
  return useSession().centerId ?? '';
}

/** Institution roster + students registered offline (pending), with pay-later fees reflected in `owes`. */
export function useRoster() {
  const q = useQuery(studentsQuery);
  const regs = usePendingRegistrations();
  const pendingCheckins = usePendingCheckins();
  const data = useMemo(() => {
    if (!q.data && !regs.length) return undefined;
    const server = q.data ?? [];
    const known = new Set(server.map((s) => s.id));
    const extra = regs.filter((r) => !known.has(r.id));
    const owesLocal = new Set(
      pendingCheckins.filter((c) => c.local.status === 'later').map((c) => c.input.studentId),
    );
    const merged = [...extra, ...server];
    return owesLocal.size ? merged.map((s) => (owesLocal.has(s.id) ? { ...s, owes: true } : s)) : merged;
  }, [q.data, regs, pendingCheckins]);
  return { ...q, data };
}

export function useActivities() {
  const centerId = useCenterId();
  const today = useToday();
  const q = useQuery({ ...activitiesQuery(centerId, today), enabled: !!centerId });
  const pending = usePendingActivities(centerId);
  const pendingCheckins = usePendingCheckins();
  const data = useMemo(() => {
    if (!q.data) return pending.length ? pending : undefined;
    const known = new Set(q.data.map((a) => a.id));
    const all = [...q.data, ...pending.filter((a) => !known.has(a.id))];
    const bump = new Map<string, number>();
    for (const c of pendingCheckins) {
      if (c.input.centerId === centerId && c.local.localDate === today) {
        bump.set(c.input.activityId, (bump.get(c.input.activityId) ?? 0) + 1);
      }
    }
    return bump.size ? all.map((a) => ({ ...a, booked: a.booked + (bump.get(a.id) ?? 0) })) : all;
  }, [q.data, pending, pendingCheckins, centerId, today]);
  return { ...q, data };
}

export function useGroups() {
  const centerId = useCenterId();
  const q = useQuery({ ...groupsQuery(centerId), enabled: !!centerId });
  const pending = usePendingGroups(centerId);
  const data = useMemo(() => {
    if (!q.data) return pending.length ? pending : undefined;
    const known = new Set(q.data.map((g) => g.id));
    return [...q.data, ...pending.filter((g) => !known.has(g.id))];
  }, [q.data, pending]);
  return { ...q, data };
}

/** Today's activity at the current center, including writes still waiting to sync. */
export function useTodayData() {
  const centerId = useCenterId();
  const date = useToday();
  const q = useQuery({ ...todayQuery(centerId, date), enabled: !!centerId });
  const checkins = usePendingCheckins();
  const rollCalls = usePendingRollCalls();
  const regs = usePendingRegistrations();
  const data = useMemo<TodayDto | undefined>(() => {
    const base: TodayDto = q.data ?? {
      date,
      checkins: [],
      rollCalls: [],
      registrations: [],
      collectedMinor: null,
    };
    if (!q.data && !checkins.length && !rollCalls.length && !regs.length) return undefined;
    const ids = new Set(base.checkins.map((c) => c.id));
    const keys = new Set(base.checkins.map((c) => `${c.studentId}|${c.activityId}`));
    const extraCheckins = [];
    for (const c of checkins) {
      const key = `${c.input.studentId}|${c.input.activityId}`;
      if (c.input.centerId !== centerId || c.local.localDate !== date || ids.has(c.input.id) || keys.has(key))
        continue;
      keys.add(key);
      extraCheckins.push({
        id: c.input.id,
        centerId,
        studentId: c.input.studentId,
        activityId: c.input.activityId,
        rollCallId: null,
        arrival: c.input.arrival,
        feeMinor: c.local.feeMinor,
        paymentStatus: c.local.status,
        staffId: c.local.staffId,
        occurredAt: c.input.occurredAt,
        localDate: date,
        pending: true,
      });
    }
    const rcIds = new Set(base.rollCalls.map((r) => r.id));
    const extraRcs = [];
    for (const r of rollCalls) {
      if (r.input.centerId !== centerId || r.local.localDate !== date || rcIds.has(r.input.id)) continue;
      extraRcs.push({
        id: r.input.id,
        centerId,
        groupId: r.input.groupId,
        activityId: r.input.activityId,
        staffId: r.local.staffId,
        presentCount: r.input.studentIds.length,
        total: r.local.total,
        occurredAt: r.input.occurredAt,
        pending: true,
      });
      for (const studentId of r.input.studentIds) {
        const key = `${studentId}|${r.input.activityId}`;
        if (keys.has(key)) continue;
        keys.add(key);
        extraCheckins.push({
          id: `${r.input.id}:${studentId}`,
          centerId,
          studentId,
          activityId: r.input.activityId,
          rollCallId: r.input.id,
          arrival: 'group' as const,
          feeMinor: r.local.feeMinor,
          paymentStatus: r.local.feeMinor > 0 ? ('later' as const) : ('free' as const),
          staffId: r.local.staffId,
          occurredAt: r.input.occurredAt,
          localDate: date,
          pending: true,
        });
      }
    }
    const regIds = new Set(base.registrations.map((r) => r.studentId));
    const extraRegs = regs
      .filter((r) => r.homeCenterId === centerId && !regIds.has(r.id) && r.localDate === date)
      .map((r) => ({ studentId: r.id, occurredAt: r.createdAt }));
    return {
      ...base,
      checkins: [...extraCheckins, ...base.checkins],
      rollCalls: [...extraRcs, ...base.rollCalls],
      registrations: [...extraRegs, ...base.registrations],
    };
  }, [q.data, checkins, rollCalls, regs, centerId, date]);
  return { ...q, data };
}
